import api from '@/modules/api.service';
import { BuscarTodasLotacoesDefensor } from './relatorios-atividades.service';
import { RelatorioAtividadeDto } from './relatorios-atividades.service';

/**
 * Identifica a Diretoria Regional responsável por aprovar o relatório
 * baseado na lotação do defensor na defensoria do relatório
 */
export async function IdentificarDiretoriaRegional(
  defensorId: number,
  defensoriaId: number
): Promise<number | null> {
  try {
    // 1. Buscar todas as lotações do defensor
    const lotacoes = await BuscarTodasLotacoesDefensor(defensorId);

    // 2. Encontrar lotação ativa na defensoria específica
    const hoje = new Date();
    const lotacaoNaDefensoria = lotacoes.find((lotacao: any) => {
      const estaNaDefensoria = lotacao.defensoria?.id === defensoriaId;
      const estaAtiva = lotacao.ativo === true;
      const dataFinal = lotacao.data_final ? new Date(lotacao.data_final) : null;
      const estaVigente = !dataFinal || dataFinal >= hoje;

      return estaNaDefensoria && estaAtiva && estaVigente;
    });

    if (!lotacaoNaDefensoria) {
      console.warn(`Lotação não encontrada para defensor ${defensorId} na defensoria ${defensoriaId}`);
      return null;
    }

    // Observação: os DTOs atuais nem sempre incluem "diretoria" aninhada em defensoria/núcleo.
    // Para não quebrar o build, acessamos de forma segura via `any` quando existir no payload.
    const defensoriaAny = (lotacaoNaDefensoria as any).defensoria as any;
    const nucleoAny = (lotacaoNaDefensoria as any).nucleo as any;

    // 3. Tentar obter diretoria através da defensoria -> núcleo -> diretoria
    const diretoriaViaDefensoriaNucleoId = defensoriaAny?.nucleo?.diretoria?.id;
    if (typeof diretoriaViaDefensoriaNucleoId === 'number') return diretoriaViaDefensoriaNucleoId;

    // 4. Tentar obter diretoria diretamente da defensoria
    const diretoriaViaDefensoriaId = defensoriaAny?.diretoria?.id;
    if (typeof diretoriaViaDefensoriaId === 'number') return diretoriaViaDefensoriaId;

    // 5. Tentar obter diretoria através do núcleo (se lotação for em núcleo)
    const diretoriaViaNucleoId = nucleoAny?.diretoria?.id;
    if (typeof diretoriaViaNucleoId === 'number') return diretoriaViaNucleoId;

    // 5. Fallback: buscar diretoria padrão ou retornar null
    // TODO: Implementar lógica de fallback ou criar diretoria padrão
    console.warn(`Diretoria regional não encontrada para defensoria ${defensoriaId}`);
    return null;
  } catch (error) {
    console.error('Erro ao identificar diretoria regional:', error);
    return null;
  }
}

/**
 * Busca relatórios enviados para aprovação de uma diretoria regional
 */
export async function BuscarRelatoriosParaAprovacao(
  diretoriaId: number,
  filters?: {
    defensorId?: number;
    defensoriaId?: number;
    mes?: string;
    ano?: number;
    tipo?: 'civil' | 'criminal';
    status?: 'enviado' | 'aprovado' | 'reprovado';
  }
): Promise<RelatorioAtividadeDto[]> {
  try {
    const params: any = {
      diretoriaId: diretoriaId,
    };

    if (filters?.defensorId) params.defensorId = filters.defensorId;
    if (filters?.defensoriaId) params.defensoriaId = filters.defensoriaId;
    if (filters?.mes) params.mes = filters.mes;
    if (filters?.ano) params.ano = filters.ano;
    if (filters?.tipo) params.tipo = filters.tipo;
    if (filters?.status) params.status = filters.status;

    const response = await api.get(`/relatorios/atividades/aprovacao`, { params });
    const relatorios = response.data || [];

    return relatorios.map((r: any) => ({
      ...r,
      dataCriacao: r.criadoEm ? new Date(r.criadoEm).toISOString() : '',
      dataEnvio: r.dataEnvio ? new Date(r.dataEnvio).toISOString() : undefined,
      dataAprovacao: r.dataAprovacao ? new Date(r.dataAprovacao).toISOString() : undefined,
      dataReprovacao: r.dataReprovacao ? new Date(r.dataReprovacao).toISOString() : undefined,
      dataAtualizacao: r.editadoEm ? new Date(r.editadoEm).toISOString() : undefined,
    }));
  } catch (error) {
    console.error('Erro ao buscar relatórios para aprovação:', error);
    return [];
  }
}

/**
 * Aprova um relatório
 */
export async function AprovarRelatorio(
  relatorioId: number,
  diretoriaId: number
): Promise<RelatorioAtividadeDto> {
  try {
    const response = await api.post(`/relatorios/atividades/${relatorioId}/aprovar`, {
      diretoriaId: diretoriaId,
    });

    const relatorio = response.data;
    return {
      ...relatorio,
      dataCriacao: relatorio.criadoEm ? new Date(relatorio.criadoEm).toISOString() : '',
      dataEnvio: relatorio.dataEnvio ? new Date(relatorio.dataEnvio).toISOString() : undefined,
      dataAprovacao: relatorio.dataAprovacao ? new Date(relatorio.dataAprovacao).toISOString() : undefined,
      dataAtualizacao: relatorio.editadoEm ? new Date(relatorio.editadoEm).toISOString() : undefined,
    };
  } catch (error) {
    console.error('Erro ao aprovar relatório:', error);
    throw error;
  }
}

/**
 * Reprova um relatório com justificativa
 */
export async function ReprovarRelatorio(
  relatorioId: number,
  diretoriaId: number,
  justificativa: string
): Promise<RelatorioAtividadeDto> {
  try {
    if (!justificativa || justificativa.trim().length === 0) {
      throw new Error('Justificativa é obrigatória para reprovação');
    }

    const response = await api.post(`/relatorios/atividades/${relatorioId}/reprovar`, {
      diretoriaId: diretoriaId,
      justificativa: justificativa.trim(),
    });

    const relatorio = response.data;
    return {
      ...relatorio,
      dataCriacao: relatorio.criadoEm ? new Date(relatorio.criadoEm).toISOString() : '',
      dataEnvio: relatorio.dataEnvio ? new Date(relatorio.dataEnvio).toISOString() : undefined,
      dataReprovacao: relatorio.dataReprovacao ? new Date(relatorio.dataReprovacao).toISOString() : undefined,
      dataAtualizacao: relatorio.editadoEm ? new Date(relatorio.editadoEm).toISOString() : undefined,
    };
  } catch (error) {
    console.error('Erro ao reprovar relatório:', error);
    throw error;
  }
}
